/**
 * The History panel (FD §3.3): versions grouped by day, a preview of the one picked, "Make this the
 * current plan", and "Save a version now" with a name.
 *
 * Restoring saves pending changes first, asks the server to restore (which keeps the current state
 * as "Before restore"), then hands the plan as it now is to `onRestored`.
 */
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import * as api from '../../api';
import type { GetPlanResponse, GetVersionResponse, PlanDoc, PlanId, VersionMeta } from '../../../shared/types';
import { useAutosaveStatus, type Autosave } from '../../store/autosave';
import { ConfirmDialog, PromptDialog, errorText, toast } from './dialogs';
import { clock, dayHeading, timeAgo } from './format';
import { PlanSketch, planSummary } from './PlanSketch';

export interface HistoryPanelProps {
  planId: PlanId;
  /** The open plan's autosave: used to save pending changes before versions are made or restored. */
  autosave: Autosave;
  /** Called after a version was restored, with the plan as it is now on the server. */
  onRestored: (plan: GetPlanResponse) => void | Promise<void>;
  onClose: () => void;
  /** Draws a preview of a version (B6 passes the real renderer); defaults to a simple sketch. */
  renderPreview?: (doc: PlanDoc) => ReactNode;
}

/** The version history side panel. */
export function HistoryPanel({ planId, autosave, onRestored, onClose, renderPreview }: HistoryPanelProps) {
  const [versions, setVersions] = useState<VersionMeta[] | null>(null);
  const [error, setError] = useState('');
  const [picked, setPicked] = useState<VersionMeta | null>(null);
  const [preview, setPreview] = useState<GetVersionResponse | null>(null);
  const [previewError, setPreviewError] = useState('');
  const [asking, setAsking] = useState<'name' | 'restore' | null>(null);

  const load = useCallback(async () => {
    try {
      setVersions(await api.listVersions(planId));
      setError('');
    } catch (e) {
      setError(errorText(e));
    }
  }, [planId]);

  // Reload after each save too: saves can add versions (automatic ones, or the other person's
  // state kept when this save replaced it).
  const { lastSavedAt } = useAutosaveStatus(autosave);
  useEffect(() => {
    void load();
  }, [load, lastSavedAt]);

  useEffect(() => {
    if (!picked) return;
    let live = true;
    setPreview(null);
    setPreviewError('');
    api.getVersion(planId, picked.vid).then(
      (v) => live && setPreview(v),
      (e) => live && setPreviewError(errorText(e)),
    );
    return () => {
      live = false;
    };
  }, [planId, picked]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && !asking && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, asking]);

  const groups: { day: string; items: VersionMeta[] }[] = [];
  for (const v of versions ?? []) {
    const day = dayHeading(v.at);
    const last = groups[groups.length - 1];
    if (last?.day === day) last.items.push(v);
    else groups.push({ day, items: [v] });
  }

  return (
    <aside className="side-panel" aria-label="History">
      <div className="side-head">
        <h2>History</h2>
        <button type="button" className="icon-btn" aria-label="Close history" onClick={onClose}>✕</button>
      </div>
      <button type="button" className="primary" onClick={() => setAsking('name')}>Save a version now…</button>
      <p className="hint">
        A version is kept automatically every 15 minutes of editing and when you close the plan. Pick one to see it.
      </p>

      {error && <p className="error">{error} <button type="button" className="link" onClick={() => void load()}>Try again</button></p>}
      {versions === null && !error && <p className="muted">Loading…</p>}
      {versions?.length === 0 && <p className="muted">No versions yet.</p>}

      <div className="versions">
        {groups.map((g) => (
          <section key={g.day}>
            <h3>{g.day}</h3>
            <ul>
              {g.items.map((v) => (
                <li key={v.vid}>
                  <button
                    type="button"
                    className={`version${picked?.vid === v.vid ? ' picked' : ''}`}
                    onClick={() => setPicked(picked?.vid === v.vid ? null : v)}
                  >
                    <span className="version-label">{versionTitle(v)}</span>
                    <span className="muted">
                      {clock(v.at)}{v.by ? ` · ${v.by}` : ''}
                    </span>
                  </button>
                  {picked?.vid === v.vid && (
                    <div className="version-preview">
                      {previewError && <p className="error">{previewError}</p>}
                      {!preview && !previewError && <p className="muted">Loading…</p>}
                      {preview && (
                        <>
                          {renderPreview ? renderPreview(preview.doc) : <PlanSketch doc={preview.doc} />}
                          <p className="muted small">{planSummary(preview.doc)}</p>
                          <button type="button" className="primary" onClick={() => setAsking('restore')}>
                            Make this the current plan
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>

      {asking === 'name' && (
        <PromptDialog
          title="Save a version"
          label="Give it a name (optional)"
          placeholder="e.g. before the big river change"
          confirmLabel="Save version"
          allowEmpty
          onClose={() => setAsking(null)}
          onSubmit={async (label) => {
            await autosave.saveVersion(label);
            toast('Version saved');
            await load();
          }}
        />
      )}
      {asking === 'restore' && picked && (
        <ConfirmDialog
          title="Go back to this version?"
          confirmLabel="Make it the current plan"
          onClose={() => setAsking(null)}
          onConfirm={async () => {
            await autosave.flush();
            await api.restoreVersion(planId, picked.vid);
            const plan = await api.getPlan(planId);
            await onRestored(plan);
            toast(`Back to the version from ${timeAgo(picked.at)}`);
            setPicked(null);
            await load();
          }}
        >
          <p>
            The plan goes back to how it was {timeAgo(picked.at)}. What you have now is kept in History as
            “Before restore”, so you can always come back to it.
          </p>
        </ConfirmDialog>
      )}
    </aside>
  );
}

/** The words shown for a version. The server labels overwritten states "Overwritten by <saver>". */
export function versionTitle(v: VersionMeta): string {
  if (v.auto) return 'Kept automatically';
  const m = /^Overwritten by (.+)$/.exec(v.label);
  if (m) return v.by ? `${v.by}'s changes, before ${m[1]} saved over them` : `Changes before ${m[1]} saved over them`;
  return v.label;
}
